import { API_BASE_URL } from '../api';

export type CreatePaymentIntentPayload = {
  type: 'booking' | 'voucher' | 'package';
  booking_id?: number;
  voucher_id?: number;
  package_purchase_id?: number;
  amount_cents?: number;
  customer_email?: string;
  description?: string;
  promo_code?: string;
};

export type CreatePaymentIntentResponse = {
  client_secret: string;
  payment_intent_id: string;
};

export type PaymentOverviewItem = {
  id: string; // booking-<id> or voucher-<id> or package-<id>
  entity_id: number;
  type: 'booking' | 'voucher' | 'package';
  local_status: 'pending' | 'paid' | 'failed' | string;
  amount_cents?: number | null;
  service_name?: string | null;
  package_name?: string | null;
  customer_name?: string | null;
  customer_email?: string | null;
  voucher_number?: string | null;
  created_at?: string | null;
  stripe_payment_intent_id?: string | null;
  stripe_status?: string;
  stripe_amount?: number;
  stripe_currency?: string;
  stripe_description?: string;
  stripe_created?: number;
  stripe_error?: string;
};

export type PaymentsOverviewResponse = {
  total_bookings: number;
  total_vouchers: number;
  total_packages: number;
  count: number;
  items: PaymentOverviewItem[];
};

export const paymentService = {
  async createPaymentIntent(payload: CreatePaymentIntentPayload): Promise<CreatePaymentIntentResponse> {
    const res = await fetch(`${API_BASE_URL}/api/payments/create-intent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const errText = await res.text();
      throw new Error(errText || 'Erreur lors de la création du paiement');
    }
    return res.json();
  },

  async getOverview(params: {
    p_type?: 'booking' | 'voucher' | 'package' | 'all';
    status?: 'pending' | 'paid' | 'failed';
    q?: string;
    include_stripe?: boolean;
    limit?: number;
    offset?: number;
  } = {}): Promise<PaymentsOverviewResponse> {
    const p = new URLSearchParams();
    if (params.p_type) p.set('p_type', params.p_type);
    if (params.status) p.set('status', params.status);
    if (params.q) p.set('q', params.q);
    if (typeof params.include_stripe === 'boolean') p.set('include_stripe', String(params.include_stripe));
    if (typeof params.limit === 'number') p.set('limit', String(params.limit));
    if (typeof params.offset === 'number') p.set('offset', String(params.offset));
    const res = await fetch(`${API_BASE_URL}/api/payments/overview?${p.toString()}`);
    if (!res.ok) {
      const errText = await res.text();
      throw new Error(errText || 'Erreur lors du chargement des paiements');
    }
    return res.json();
  },

  async getStripeDetails(payment_intent_id: string): Promise<any> {
    const res = await fetch(`${API_BASE_URL}/api/payments/stripe/${payment_intent_id}`);
    if (!res.ok) {
      const errText = await res.text();
      throw new Error(errText || 'Erreur lors de la récupération des détails Stripe');
    }
    return res.json();
  },

  async markBookingPaid(bookingId: number, stripePaymentIntentId: string): Promise<any> {
    const res = await fetch(`${API_BASE_URL}/api/bookings/${bookingId}/mark-paid?stripe_payment_intent_id=${encodeURIComponent(stripePaymentIntentId)}`, {
      method: 'PUT',
    });
    if (!res.ok) {
      const errText = await res.text();
      throw new Error(errText || 'Erreur lors de la confirmation du paiement');
    }
    return res.json();
  },

  async resync(p_type: 'booking' | 'voucher' | 'package', entity_id: number): Promise<any> {
    const res = await fetch(`${API_BASE_URL}/api/payments/resync/${p_type}/${entity_id}`, {
      method: 'POST'
    });
    if (!res.ok) {
      const errText = await res.text();
      throw new Error(errText || 'Erreur lors de la resynchronisation');
    }
    return res.json();
  }
};
